import React from 'react';
import { ShieldCheck, Layers, Crosshair, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

interface ServiceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ title, description, icon, delay }) => (
  <motion.div 
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.5, delay }}
    className="bg-zinc-900 p-8 rounded-[2rem] border border-zinc-800 hover:border-primary/50 hover:bg-zinc-900/80 transition-all duration-300 h-full flex flex-col group relative overflow-hidden"
  >
    {/* Gradient overlay on hover */}
    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    
    <div className="relative z-10">
      <div className="w-14 h-14 bg-zinc-800 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors duration-300">
        <div className="text-white group-hover:text-primary transition-colors duration-300">
          {icon}
        </div>
      </div>
      <h3 className="font-bold text-xl text-white mb-3">{title}</h3>
      <p className="text-muted leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

const Services: React.FC = () => {
  return (
    <section className="py-24 2xl:px-52 px-0 bg-navy text-white">
      <div className="lg:px-24 px-6 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="font-bold text-3xl md:text-5xl text-white mb-4">
            The premium choice for games<br/>serious about growth.
          </h2>
          <p className="text-muted text-lg md:text-xl">
            We're not just creators. We're growth partners.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ServiceCard 
            delay={0.1}
            title="View Guarantees"
            description="Unlike other agencies, we guarantee view counts. If we don't hit the target, you don't pay."
            icon={<ShieldCheck size={28} />}
          />
          <ServiceCard 
            delay={0.2}
            title="Full Management"
            description="We manage your entire social presence. From concept to posting, we handle everything."
            icon={<Layers size={28} />}
          />
          <ServiceCard 
            delay={0.3}
            title="Gaming Specialists"
            description="We only work in gaming. We know Minecraft, Roblox, and Fortnite ecosystems inside out."
            icon={<Crosshair size={28} />}
          />
          <ServiceCard 
            delay={0.4}
            title="Viral Mechanics"
            description="We use proven frameworks for hook psychology and retention to beat the algorithms."
            icon={<TrendingUp size={28} />}
          />
        </div>
      </div>
    </section>
  );
};

export default Services;
